import styled from '@emotion/styled';
import { Spinner } from '@anyroad/spinner';

import { Deposit } from './Deposit';
import { Breakdown } from './Breakdown';
import { Remainder } from './Remainder';
import { Subtotal } from './Subtotal';
import { CashPaymentNotice } from './CashPaymentNotice';
import type { PricingProps } from './types';

const Container = styled.div`
  .CheckoutSummary-Subtotal-PriceBreakdown-Deposit {
  }
`;

const ContentBox = styled.div`
  &.Subtotal-PriceBreakdown {
  }
`;

export const Pricing = ({
  expandedPriceBreakdown = false,
  displaySubtotalIcon = true,
  onTogglePriceBreakdown,
  date,
  forZeroPricedTour = false,
  bookingFeeFormatted,
  discountFormatted,
  discountCode,
  onlineDiscountFormatted,
  depositFormatted,
  guestsPriceFormatted,
  totalFormatted,
  additionalTax,
  additionalTaxFormatted,
  additionalTaxLabel,
  remainderFormatted,
  remainderPaymentDate,
  remainderInCash,
  remainderChargeDaysOffset,
  remainderInLocalCurrencyFormatted,
  totalInLocalCurrency,
  totalInLocalCurrencyFormatted,
  totalInLocalCurrencyExactFormatted,
  paidInCash,
  addons = {},
  units = {},
}: PricingProps) => {
  const includesBookingFee = bookingFeeFormatted
    ? parseFloat(bookingFeeFormatted.replace(/[^0-9.-]+/g, '')) > 0
    : false;
  const includesDiscount = discountFormatted
    ? parseFloat(discountFormatted.replace(/[^0-9.-]+/g, '')) > 0
    : false;
  const includesOnlineDiscount = onlineDiscountFormatted
    ? parseFloat(onlineDiscountFormatted.replace(/[^0-9.-]+/g, '')) > 0
    : false;
  const requiresDeposit = depositFormatted
    ? parseFloat(depositFormatted.replace(/[^0-9.-]+/g, '')) > 0
    : false;

  const breakdownProps = {
    bookingFeeFormatted,
    includesBookingFee,
    includesDiscount,
    discountFormatted,
    discountCode,
    onlineDiscountFormatted,
    includesOnlineDiscount,
    includesAdditionalTax: (additionalTax ?? 0) > 0,
    additionalTaxFormatted,
    additionalTaxLabel,
    guestsPriceFormatted,
    units,
    addons: addons as any,
  };

  const remainderProps = {
    remainderFormatted: remainderFormatted!,
    remainderInLocalCurrencyFormatted,
    remainderPaymentDate,
    remainderInCash,
    remainderChargeDaysOffset,
    date,
  };

  const shouldRenderDepositRemainder =
    !paidInCash &&
    requiresDeposit &&
    depositFormatted !== totalInLocalCurrencyExactFormatted;

  if (!totalInLocalCurrency && forZeroPricedTour) {
    return null;
  }

  if (!totalInLocalCurrency) {
    return <Spinner height={60} />;
  }

  if (!forZeroPricedTour || Number(totalInLocalCurrency) !== 0) {
    return (
      <Container>
        <div className="CheckoutSummary-Subtotal-PriceBreakdown-Deposit">
          <ContentBox className="CheckoutSummary-ContentBox Subtotal-PriceBreakdown">
            {expandedPriceBreakdown ? (
              <div>
                <Subtotal
                  displayIcon={displaySubtotalIcon}
                  amount={totalFormatted}
                  onToggle={onTogglePriceBreakdown}
                />
                <Breakdown {...breakdownProps} />
              </div>
            ) : (
              <Subtotal
                displayIcon={displaySubtotalIcon}
                amount={totalFormatted}
                onToggle={onTogglePriceBreakdown}
              />
            )}
          </ContentBox>
          {shouldRenderDepositRemainder && depositFormatted && (
            <Deposit amount={depositFormatted} />
          )}
          {shouldRenderDepositRemainder && remainderFormatted && (
            <Remainder {...remainderProps} />
          )}
          {paidInCash && (
            <CashPaymentNotice
              totalInLocalCurrencyFormatted={totalInLocalCurrencyFormatted}
              totalFormatted={totalFormatted}
              date={date}
            />
          )}
        </div>
      </Container>
    );
  }

  return null;
};
